import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { navigate, onLink } from "../lib/router.ts";
import { useSession } from "../lib/session.tsx";
import { Icon, type IconName } from "./icons.tsx";
import { Palette, type PaletteItem } from "./palette.tsx";

type Section = "screens" | "data" | "changes" | "workflows" | "members" | "roles" | "ai" | "billing";

interface RailItem {
  id: Section;
  label: string;
  icon: IconName;
  /** Opens a list (screens, data) instead of going somewhere. */
  list?: boolean;
  href?: string;
  badge?: number;
}

/** Which rail item a path belongs to. */
export function sectionOf(path: string): Section {
  if (path.startsWith("/dados/")) return "data";
  if (path.startsWith("/changesets")) return "changes";
  if (path === "/workflows") return "workflows";
  if (path === "/membros") return "members";
  if (path === "/papeis") return "roles";
  if (path === "/conexoes") return "ai";
  if (path === "/cobranca") return "billing";
  return "screens";
}

const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);

/**
 * The "Ilha" shell from the design system: a dark icon rail that stays dark in both
 * themes, the store's content on a light island, and the command bar (⌘K) on top.
 * On phones the rail becomes a floating dock at the bottom.
 */
export function IlhaShell({ path, children }: { path: string; children: ReactNode }) {
  const s = useSession();
  const [list, setList] = useState<"screens" | "data" | "more" | null>(null);
  const [pal, setPal] = useState(false);
  const [stores, setStores] = useState(false);
  const active = sectionOf(path);

  useEffect(() => (setList(null), setStores(false)), [path, s.tenant?.id]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPal((v) => !v);
      } else if (e.key === "Escape") {
        setList(null);
        setStores(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const rail: RailItem[] = useMemo(() => {
    if (!s.tenant) return [];
    const r: RailItem[] = [
      { id: "screens", label: "Telas", icon: "screens", list: true },
      { id: "data", label: "Dados", icon: "data", list: true },
    ];
    if (s.isStaff) r.push({ id: "changes", label: "Mudanças", icon: "changes", href: "/changesets", badge: s.pending || undefined });
    if (s.isStaff) r.push({ id: "workflows", label: "Workflows", icon: "workflows", href: "/workflows" });
    if (s.isOwner) r.push({ id: "members", label: "Membros", icon: "members", href: "/membros" });
    if (s.isStaff) r.push({ id: "roles", label: "Papéis", icon: "roles", href: "/papeis" });
    r.push({ id: "ai", label: "Conexões de IA", icon: "ai", href: "/conexoes" });
    if (s.isOwner) r.push({ id: "billing", label: "Cobrança", icon: "billing", href: "/cobranca" });
    return r;
  }, [s.tenant, s.isStaff, s.isOwner, s.pending]);

  const menuPages = s.pages.filter((p) => p.menu);

  const paletteItems: PaletteItem[] = useMemo(() => {
    const out: PaletteItem[] = [];
    for (const p of menuPages) out.push({ group: "Telas", label: p.title, icon: "screens", href: `/t/${p.name}` });
    for (const c of s.collections) out.push({ group: "Dados", label: c.label, icon: "data", href: `/dados/${c.name}` });
    for (const r of rail) if (r.href) out.push({ group: "Loja", label: r.label, icon: r.icon, href: r.href });
    if (s.canBuild) out.push({ group: "Ações", label: "Nova tela", icon: "plus", href: "/nova-tela" });
    for (const t of s.tenants) if (t.id !== s.tenant?.id) out.push({ group: "Ações", label: `Trocar para ${t.name}`, icon: "store", run: () => (s.switchTenant(t.id), navigate("/")) });
    out.push({ group: "Ações", label: "Nova loja", icon: "store", href: "/lojas/nova" });
    return out;
  }, [menuPages, s, rail]);

  const pick = (r: RailItem) => {
    if (r.list) setList(list === r.id ? null : (r.id as "screens" | "data"));
    else if (r.href) navigate(r.href);
  };

  return (
    <div className="ilha">
      <Rail items={rail} active={list && list !== "more" ? list : active} onPick={pick} account={s.account.name} onLogout={() => s.logout()} />

      {list && list !== "more" && (
        <ListPanel kind={list} onClose={() => setList(null)}>
          {list === "screens" ? (
            <>
              {menuPages.map((p) => (
                <a key={p.id} href={`/t/${p.name}`} onClick={onLink} className={path === `/t/${p.name}` ? "on" : ""}>
                  <Icon name="screens" size={16} />
                  {p.title}
                </a>
              ))}
              {!menuPages.length && <p className="muted">Nenhuma tela ainda.</p>}
              {s.canBuild && (
                <a href="/nova-tela" onClick={onLink} className="add">
                  <Icon name="plus" size={16} />
                  Nova tela
                </a>
              )}
            </>
          ) : (
            <>
              {s.collections.map((c) => (
                <a key={c.id} href={`/dados/${c.name}`} onClick={onLink} className={path === `/dados/${c.name}` ? "on" : ""}>
                  <Icon name="data" size={16} />
                  {c.label}
                </a>
              ))}
              {!s.collections.length && <p className="muted">Nenhuma coleção ainda. Peça à sua IA ou crie pelo editor de telas.</p>}
            </>
          )}
        </ListPanel>
      )}

      <div className="island">
        <header className="itop">
          {s.tenants.length > 0 && (
            <div className="store-pick">
              <button className="store-btn" aria-expanded={stores} aria-haspopup="menu" onClick={() => setStores((v) => !v)}>
                <span className="av">{initials(s.tenant?.name ?? "")}</span>
                <span className="nm">{s.tenant?.name ?? "Escolha uma loja"}</span>
                <Icon name="chev" size={16} />
              </button>
              {stores && (
                <div className="store-menu" role="menu">
                  {s.tenants.map((t) => (
                    <button
                      key={t.id}
                      role="menuitemradio"
                      aria-checked={t.id === s.tenant?.id}
                      onClick={() => (setStores(false), s.switchTenant(t.id), navigate("/"))}
                    >
                      <span className="av">{initials(t.name)}</span>
                      <span className="nm">{t.name}</span>
                      <small className="mono">varejoon.com/{t.slug}</small>
                    </button>
                  ))}
                  <button role="menuitem" className="new" onClick={() => (setStores(false), navigate("/lojas/nova"))}>
                    <Icon name="plus" size={16} />
                    Nova loja
                  </button>
                </div>
              )}
            </div>
          )}
          <button className="cmd" onClick={() => setPal(true)} aria-label="Buscar ou pedir à IA">
            <Icon name="search" size={18} />
            <span className="grow">Buscar ou pedir à IA</span>
            <kbd>{isMac ? "⌘K" : "Ctrl K"}</kbd>
          </button>
        </header>
        <div className="icontent">
          {s.tenant?.readOnly && (
            <div className="banner" role="status">
              Esta loja está só leitura porque há uma fatura vencida há mais de 7 dias. Nada foi apagado.{" "}
              {s.isOwner ? (
                <a href="/cobranca" onClick={onLink}>
                  Pagar com Pix
                </a>
              ) : (
                "Peça ao dono da loja para pagar em Cobrança."
              )}
            </div>
          )}
          {children}
        </div>
      </div>

      {rail.length > 0 && (
        <Dock
          items={rail}
          active={list ?? active}
          onPick={pick}
          onSearch={() => setPal(true)}
          onMore={() => setList(list === "more" ? null : "more")}
        />
      )}
      {list === "more" && (
        <ListPanel kind="more" onClose={() => setList(null)}>
          {rail.slice(2).map((r) => (
            <a key={r.id} href={r.href} onClick={onLink} className={active === r.id ? "on" : ""}>
              <Icon name={r.icon} size={16} />
              {r.label}
              {r.badge ? <span className="count">{r.badge}</span> : null}
            </a>
          ))}
          <button className="add" onClick={() => s.logout()}>
            <Icon name="logout" size={16} />
            Sair ({s.account.name})
          </button>
        </ListPanel>
      )}

      <Palette items={paletteItems} open={pal} onClose={() => setPal(false)} />
    </div>
  );
}

function Rail({ items, active, onPick, account, onLogout }: { items: RailItem[]; active: string; onPick: (r: RailItem) => void; account: string; onLogout: () => void }) {
  const nav = useRef<HTMLElement>(null);
  const [hole, setHole] = useState<number | null>(null);
  const [me, setMe] = useState(false);
  useEffect(() => {
    if (!me) return;
    const close = (e: Event) => {
      if (!(e.target as Element).closest?.(".me-wrap") || (e as KeyboardEvent).key === "Escape") setMe(false);
    };
    document.addEventListener("pointerdown", close);
    document.addEventListener("keydown", close);
    return () => (document.removeEventListener("pointerdown", close), document.removeEventListener("keydown", close));
  }, [me]);
  useLayoutEffect(() => {
    const b = nav.current?.querySelector<HTMLElement>(`[data-id="${active}"]`);
    setHole(b ? b.offsetTop + b.offsetHeight / 2 - 4 : null);
  }, [active, items]);
  return (
    <nav className="rail" aria-label="Navegação" ref={nav}>
      <a className="logo" href="/" onClick={onLink} aria-label="Varejoon, início">
        V
      </a>
      {items.map((r) => (
        <button key={r.id} data-id={r.id} className={`nb ${active === r.id ? "on" : ""}`} aria-current={active === r.id ? "page" : undefined} onClick={() => onPick(r)}>
          <Icon name={r.icon} />
          <span className="tip">{r.label}</span>
          {r.badge ? <span className="badge">{r.badge}</span> : null}
        </button>
      ))}
      {hole !== null && <span className="hole" style={{ top: hole }} aria-hidden="true" />}
      <span className="grow" />
      <div className="me-wrap">
        <button className="nb me" aria-haspopup="menu" aria-expanded={me} onClick={() => setMe((v) => !v)}>
          <span className="av">{initials(account)}</span>
          {!me && <span className="tip">{account}</span>}
        </button>
        {me && (
          <div className="me-menu" role="menu">
            <strong>{account}</strong>
            <button role="menuitem" onClick={onLogout}>
              <Icon name="logout" size={16} />
              Sair
            </button>
          </div>
        )}
      </div>
    </nav>
  );
}

function Dock({ items, active, onPick, onSearch, onMore }: { items: RailItem[]; active: string; onPick: (r: RailItem) => void; onSearch: () => void; onMore: () => void }) {
  const main = items.slice(0, 2);
  const restActive = !main.some((r) => r.id === active);
  const badge = items.slice(2).reduce((n, r) => n + (r.badge ?? 0), 0);
  return (
    <nav className="dock" aria-label="Navegação">
      {main.map((r) => (
        <button key={r.id} className={active === r.id ? "on" : ""} onClick={() => onPick(r)}>
          <Icon name={r.icon} />
          <span>{r.label}</span>
        </button>
      ))}
      <button className="find" onClick={onSearch} aria-label="Buscar ou pedir à IA">
        <Icon name="search" />
      </button>
      <button className={restActive ? "on" : ""} onClick={onMore}>
        <Icon name="more" />
        <span>Mais</span>
        {badge ? <span className="badge">{badge}</span> : null}
      </button>
    </nav>
  );
}

function ListPanel({ kind, onClose, children }: { kind: "screens" | "data" | "more"; onClose: () => void; children: ReactNode }) {
  const title = kind === "screens" ? "Telas" : kind === "data" ? "Dados" : "Loja";
  return (
    <>
      <div className="lp-scrim" onClick={onClose} />
      <aside className={`lp lp-${kind}`} aria-label={title}>
        <header>
          <h2>{title}</h2>
          <button className="x" onClick={onClose} aria-label="Fechar">
            <Icon name="close" size={16} />
          </button>
        </header>
        <div className="lp-body">{children}</div>
      </aside>
    </>
  );
}

export function initials(name: string): string {
  const w = name.trim().split(/\s+/).filter((x) => x.length > 2 || name.trim().split(/\s+/).length === 1);
  return ((w[0]?.[0] ?? "") + (w[1]?.[0] ?? "")).toUpperCase() || "?";
}
